import type { NotificationBannerRow } from "store/api/notificationsApi";

const CLOSED_DEPLOYMENT_STATUSES = new Set(["closed", "ended"]);

export const SYSTEM_BANNERS_POLL_MS = 60_000;

export function isActiveSystemBanner(banner: NotificationBannerRow): boolean {
  const status = String(banner.deployment_status ?? "")
    .trim()
    .toLowerCase();
  return !CLOSED_DEPLOYMENT_STATUSES.has(status);
}

export function selectActiveSystemBanners(
  banners: NotificationBannerRow[] | undefined
): NotificationBannerRow[] {
  if (!Array.isArray(banners) || banners.length === 0) return [];
  return banners.filter((banner) => isActiveSystemBanner(banner));
}

function bannerSortTimestamp(banner: NotificationBannerRow): number {
  const startMs = Date.parse(String(banner.banner_start_time ?? "").trim());
  if (Number.isFinite(startMs)) return startMs;
  const endMs = Date.parse(String(banner.banner_end_time ?? "").trim());
  if (Number.isFinite(endMs)) return endMs;
  return banner.banner_id;
}

export function sortSystemBannersNewestFirst(
  banners: NotificationBannerRow[]
): NotificationBannerRow[] {
  return [...banners].sort((left, right) => {
    const timeDiff = bannerSortTimestamp(right) - bannerSortTimestamp(left);
    if (timeDiff !== 0) return timeDiff;
    return right.banner_id - left.banner_id;
  });
}

export function extractSystemBannersFromResponse(
  data: { success?: boolean; data?: { banners?: NotificationBannerRow[] } } | undefined
): NotificationBannerRow[] {
  if (!data || data.success === false) return [];
  return sortSystemBannersNewestFirst(selectActiveSystemBanners(data.data?.banners));
}

export function formatBannerMenuDateTime(value?: string | null): string {
  if (!value?.trim()) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  const day = date.getDate();
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const period = hours >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 || 12;

  return `${day} ${month} ${year} ${String(hour12).padStart(2, "0")}:${minutes} ${period}`;
}

export function getDeploymentStatusBadgeColor(
  status: string
): "success" | "warning" | "error" | "gray" | "brand" {
  const value = status.toLowerCase();
  if (value === "planned" || value === "progress" || value === "in-progress") return "warning";
  if (value === "closed" || value === "ended") return "gray";
  return "brand";
}

export function systemBannerAlertStatus(
  banner: NotificationBannerRow
): "danger" | "warning" | "info" {
  const category = String(banner.deployment_category ?? "")
    .trim()
    .toLowerCase();
  if (category === "critical" || category === "high" || category === "priority") {
    return "danger";
  }
  if (category === "medium") return "warning";
  return "info";
}

export function getSystemBannerDetailItems(
  banner: NotificationBannerRow
): Array<{ label: string; value: string }> {
  const items: Array<{ label: string; value: string }> = [];
  const pushDate = (label: string, value?: string | null) => {
    const text = formatBannerMenuDateTime(value);
    if (text !== "—") items.push({ label, value: text });
  };

  pushDate("Start", banner.deployment_start_time);
  pushDate("End", banner.deployment_end_time);

  return items;
}

export function closeLayoutHelpDropdown(): void {
  const trigger = document.querySelector<HTMLButtonElement>(
    '.navbar__help-dropdown .navbar__help-button[aria-expanded="true"]'
  );
  trigger?.click();
}
